import { Link, usePage } from "@inertiajs/react";
import { InstallPrompt } from "@/components/InstallPrompt";
import type { ReactNode } from "react";
import type { SharedProps } from "@/lib/types";

export function Layout({ children, title }: { children: ReactNode; title?: string }) {
  const { currentUser, flash } = usePage<SharedProps>().props;

  return (
    <div className="min-h-full">
      <header className="border-b border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <Link href="/" className="flex items-center gap-2 font-semibold tracking-tight">
            <span aria-hidden className="text-brand-600">◉</span>
            Court&nbsp;Pilot
            {/* Sits on the wordmark rather than in a dismissible banner: it
                should still be true on the tenth visit, when a banner would
                long since have been clicked away. */}
            <span
              className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
              title="Court Pilot is still being built. It books real courts, but it can get things wrong — always check your bookings in your club’s own app."
            >
              beta
            </span>
          </Link>

          {currentUser && (
            <div className="flex items-center gap-4 text-sm">
              <Link href="/guide" className="text-neutral-600 hover:underline dark:text-neutral-300">
                Guide
              </Link>
              {currentUser.operator && (
                <>
                  <Link href="/overview" className="text-neutral-600 hover:underline dark:text-neutral-300">
                    Overview
                  </Link>
                  <Link href="/invites" className="text-neutral-600 hover:underline dark:text-neutral-300">
                    Invites
                  </Link>
                </>
              )}
              <span className="text-neutral-500 dark:text-neutral-400">
                {currentUser.name ?? currentUser.phone}
                {currentUser.club && ` · ${currentUser.club}`}
              </span>
              <Link
                href="/logout"
                method="delete"
                as="button"
                className="rounded-md px-2 py-1 text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
              >
                Sign out
              </Link>
            </div>
          )}
        </div>
      </header>

      {/* Your club refuses bookings for this member from our installation.
          Nothing they do in the app changes that, so it is stated plainly
          rather than letting them file requests that cannot be acted on. */}
      {currentUser?.blocked && (
        <div className="bg-rose-50 text-rose-900 dark:bg-rose-950 dark:text-rose-200">
          <div className="mx-auto max-w-5xl px-4 py-2 text-sm">
            Booking is paused for your account. Your club is refusing bookings
            made for you from this installation — we are working on it, and
            nothing you can do in here will fix it. You can still look around.
          </div>
        </div>
      )}

      {/* A user can sign in but still be unable to book, so say so rather than
          letting their requests fail silently. */}
      {currentUser?.needsReauth && (
        <div className="bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200">
          <div className="mx-auto max-w-5xl px-4 py-2 text-sm">
            Your club connection has expired — bookings are paused until you{" "}
            <Link href="/login" className="font-medium underline">reconnect</Link>.
          </div>
        </div>
      )}

      {/* The badge alone is decoration; this says what it means. Kept quiet
          and permanent rather than loud and dismissible. */}
      <div className="border-b border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900/60">
        <div className="mx-auto max-w-5xl px-4 py-1.5 text-xs text-neutral-500">
          Beta — Court Pilot books real courts, but it can get things wrong. Please
          check anything important in your club’s own app.
        </div>
      </div>

      {flash?.notice && <Banner tone="success">{flash.notice}</Banner>}
      {flash?.alert && <Banner tone="error">{flash.alert}</Banner>}

      <main className="mx-auto max-w-5xl px-4 py-8">
        {title && <h1 className="mb-6 text-2xl font-semibold tracking-tight">{title}</h1>}
        {children}
      </main>

      {/* Nudge to add to the home screen, once signed in — self-suppressing
          when already installed or recently dismissed. */}
      {currentUser && <InstallPrompt />}
    </div>
  );
}

function Banner({ tone, children }: { tone: "success" | "error"; children: ReactNode }) {
  const tones = {
    success: "bg-brand-50 text-brand-700 dark:bg-brand-700/15 dark:text-brand-50",
    error: "bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200",
  };

  return (
    <div className={tones[tone]} role="status">
      <div className="mx-auto max-w-5xl px-4 py-2 text-sm">{children}</div>
    </div>
  );
}
